import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function Terms() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-cyan-500 to-teal-400">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="mb-6 flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">Terms of Service</h1>
          <p className="text-gray-600 mb-8">Last updated: July 22, 2026</p>

          <div className="prose prose-lg max-w-none space-y-6 text-gray-700">
            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">1. Acceptance of Terms</h2>
              <p>
                By accessing and using 10/10 Beach Day Alerts ("Service"), you accept and agree to be bound by these Terms of Service.
                If you do not agree to these terms, please do not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">2. Description of Service</h2>
              <p>
                10/10 provides personalized beach weather alerts based on your preferred weather conditions. The Service analyzes
                weather forecasts and sends email or SMS notifications when conditions match your ideal beach day criteria.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">3. User Accounts</h2>
              <p>
                You may create an account using Google OAuth or email/password authentication. You are responsible for:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Notifying us immediately of any unauthorized use</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">4. Premium Subscription</h2>
              <p>
                Premium features are available for $2/month or $19/year and include:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Up to 3 beach locations</li>
                <li>SMS text alerts</li>
                <li>2-3 day advance alerts</li>
                <li>Morning/afternoon time preference</li>
              </ul>
              <p className="mt-3">
                All subscriptions include a 7-day free trial. You may cancel at any time. Refunds are provided on a case-by-case basis.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">5. SMS Terms</h2>
              <p>
                By enabling SMS alerts, you consent to receive text messages at the phone number you provide. Standard message and
                data rates may apply. Message frequency varies based on weather conditions (typically 0-7 messages per week).
              </p>
              <p className="mt-3">
                <strong>To opt out:</strong> Reply STOP to any SMS message. To get help, reply HELP.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">6. Weather Data Accuracy</h2>
              <p>
                Weather forecasts are provided by third-party APIs (Open-Meteo, Stormglass) and are for informational purposes only.
                We make no warranties about the accuracy, reliability, or completeness of weather data. Always check official weather
                sources before making beach plans.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">7. Prohibited Uses</h2>
              <p>You agree not to:</p>
              <ul className="list-disc ml-6 space-y-2">
                <li>Use the Service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Reverse engineer or copy any part of the Service</li>
                <li>Use automated systems to access the Service</li>
                <li>Share your account with others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">8. Termination</h2>
              <p>
                We reserve the right to suspend or terminate your account at any time for violation of these Terms.
                You may terminate your account at any time by contacting us or canceling your subscription.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">9. Limitation of Liability</h2>
              <p>
                The Service is provided "as is" without warranties of any kind. We are not liable for any damages arising from
                use of the Service, including but not limited to: missed beach days, inaccurate forecasts, failed alerts, or
                any decisions made based on our notifications.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">10. Changes to Terms</h2>
              <p>
                We may update these Terms at any time. Continued use of the Service after changes constitutes acceptance of the new Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">11. Contact</h2>
              <p>
                For questions about these Terms, contact us at:{' '}
                <a href="mailto:support@10outtaten.com" className="text-cyan-600 hover:underline">
                  support@10outtaten.com
                </a>
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
